import { useEffect, useRef, useState } from "react";
import { destinations, NavigationProps } from "../navigation";
import styles from "../styles/MobileNavigation.module.css";

/** Offers a non-modal phone menu with focus restoration and shared destinations. */
export default function MobileNavigation({ active, navigateCallback }: NavigationProps) {
	const [open, setOpen] = useState(false);
	const toggle = useRef<HTMLButtonElement>(null);
	const menu = useRef<HTMLElement>(null);
	const current = destinations.find(({ name }) => name === active)!;

	useEffect(() => {
		if (open) {
			menu.current?.querySelector<HTMLButtonElement>('[aria-current="page"]')?.focus();
		}
	}, [open]);

	useEffect(() => {
		const desktop = window.matchMedia("(min-width: 761px)");
		let lastFocused = document.activeElement;
		/** Remembers focus before responsive CSS can hide the focused navigation. */
		function trackFocus(event: FocusEvent): void {
			if (event.target instanceof Element && event.target !== document.body) {
				lastFocused = event.target;
			}
		}
		/** Keeps focus on visible controls when switching between navigation layouts. */
		function handleLayoutChange(): void {
			const focused = document.activeElement === document.body
				? lastFocused
				: document.activeElement;
			if (desktop.matches) {
				if (toggle.current?.contains(focused) || menu.current?.contains(focused)) {
					document.getElementById("main-editor")?.focus();
				}
				setOpen(false);
			} else if (focused?.closest("[data-editor-navigation]")) {
				toggle.current?.focus();
			}
		}
		document.addEventListener("focusin", trackFocus);
		desktop.addEventListener("change", handleLayoutChange);
		return () => {
			document.removeEventListener("focusin", trackFocus);
			desktop.removeEventListener("change", handleLayoutChange);
		};
	}, []);

	useEffect(() => {
		if (!open) return;
		/** Dismisses the disclosure without trapping keyboard focus. */
		function handleEscape(event: KeyboardEvent): void {
			if (event.key === "Escape") {
				setOpen(false);
				toggle.current?.focus();
			}
		}
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [open]);

	return (
		<header className={styles.mobile}>
			<div className={styles.bar}>
				<div className={styles.identity}>
					<span>Wayne Rockett</span>
					<span className={styles.filename}>{current.filename}</span>
				</div>
				<button
					ref={toggle}
					type="button"
					aria-expanded={open}
					aria-controls="mobile-navigation"
					onClick={() => setOpen(!open)}>
					{open ? "Close menu" : "Menu"}
				</button>
			</div>
			<nav
				ref={menu}
				id="mobile-navigation"
				aria-label="Site menu"
				className={styles.menu}
				hidden={!open}>
				{destinations.map(({ name, label, filename }) => (
					<button
						key={name}
						type="button"
						aria-current={active === name ? "page" : undefined}
						onClick={() => {
							navigateCallback(name);
							setOpen(false);
							document.getElementById("main-editor")?.focus();
						}}>
						<span>{label}</span>
						<span className={styles.filename}>{filename}</span>
					</button>
				))}
			</nav>
		</header>
	);
}
